import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { colors, inr } from "@/theme";
import { Spinner, EmptyState } from "@/components/common";
import { StatusPill } from "@/pages/Orders";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { toast } from "sonner";

export default function OrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(false);
  const isMobile = useIsMobile();

  const load = useCallback(() => {
    api(`/orders/${id}`).then(setOrder).catch(() => setNotFound(true));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const pay = async () => {
    setBusy(true);
    try { await api(`/orders/${id}/mock-pay`, { method: "POST" }); toast.success("Payment successful"); load(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  if (notFound) return <EmptyState title="Order not found" action={<Link to="/orders" className="btn-primary" style={{ textDecoration: "none" }}>My Orders</Link>} />;
  if (!order) return <Spinner full />;

  return (
    <div className="container-max fade-up" style={{ padding: isMobile ? "24px 16px 40px" : "40px 24px 56px", maxWidth: 760 }}>
      <div className="eyebrow" style={{ marginBottom: 8 }}><Link to="/orders" style={{ color: colors.mutedText, textDecoration: "none" }}>My Orders</Link> / #{order.id.slice(0, 8)}</div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <h1 className="font-display" style={{ fontSize: 30, fontWeight: 700, margin: 0 }}>Order #{order.id.slice(0, 8)}</h1>
        <StatusPill status={order.status} />
      </div>
      <p className="font-body" style={{ color: colors.mutedText, marginTop: 6 }}>Placed {new Date(order.created_at).toLocaleString()}</p>

      {order.status === "created" && (
        <div style={{ background: colors.accentLight, border: `1px solid ${colors.accent}`, borderRadius: 12, padding: 18, marginTop: 16, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <span className="font-body" style={{ color: colors.onAccent, fontWeight: 500 }}>Payment pending for this order.</span>
          <button data-testid="pay-now" className="btn-brand" onClick={pay} disabled={busy}>{busy ? "Processing…" : `Pay ${inr(order.amount)}`}</button>
        </div>
      )}

      <div style={{ marginTop: 24, border: `1px solid ${colors.border}`, borderRadius: 12, overflow: "hidden" }}>
        {order.items.map((it) => (
          <div key={it.product_id} style={{ display: "flex", gap: 14, padding: 16, borderBottom: `1px solid ${colors.border}` }}>
            {it.image && <img src={it.image} alt="" style={{ width: 60, height: 60, objectFit: "cover", borderRadius: 8 }} />}
            <div style={{ flex: 1 }}>
              <div className="font-body" style={{ fontWeight: 600 }}>{it.name}</div>
              <div className="font-body" style={{ fontSize: 13, color: colors.mutedText }}>Qty {it.quantity} · {inr(it.price)}</div>
            </div>
            <div className="font-body" style={{ fontWeight: 700 }}>{inr(it.line_total)}</div>
          </div>
        ))}
        <div style={{ padding: 16, background: colors.surfaceSecondary }}>
          <Row label="Subtotal" value={inr(order.subtotal)} />
          {order.coupon_discount > 0 && <Row label="Coupon" value={`−${inr(order.coupon_discount)}`} />}
          {order.points_discount > 0 && <Row label="Points" value={`−${inr(order.points_discount)}`} />}
          {order.gift_wrap_fee > 0 && <Row label="Gift wrap" value={inr(order.gift_wrap_fee)} />}
          <div style={{ borderTop: `1px solid ${colors.border}`, margin: "10px 0" }} />
          <Row label="Total" value={inr(order.amount)} bold />
        </div>
      </div>

      <div style={{ marginTop: 24 }}>
        <div className="eyebrow" style={{ marginBottom: 8 }}>Shipping To</div>
        <p className="font-body" style={{ margin: 0, lineHeight: 1.6 }}>{order.shipping_name}<br />{order.shipping_phone}<br />{order.shipping_address}</p>
      </div>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
      <span className="font-body" style={{ fontWeight: bold ? 700 : 400, fontSize: bold ? 16 : 14 }}>{label}</span>
      <span className="font-body" style={{ fontWeight: bold ? 700 : 500, fontSize: bold ? 16 : 14 }}>{value}</span>
    </div>
  );
}
